import { capitalize, uniq } from 'es-toolkit';
import { produce } from 'immer';

import { ReadFile } from '@paragon/client-tools';
import { Insertion, ProjectSynchronizationPayload } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  changeInsertionQuantity,
  getInsertionsWithPendingChangesGroupedByObjectGuid,
  migrateIncomingLayoutModel,
  projectSynchronized,
  saveAllProjectChanges,
} from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const downloadProject =
  ({ layoutOnly }: { layoutOnly: boolean } = { layoutOnly: false }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const { openContext } = state.application;
    if (openContext?.type !== 'project') {
      dispatch(enqueueError('Cannot get layout data outside of a project context.'));
      return;
    }
    const { projectGuid } = openContext;

    const spinnerId = dispatch(addSpinnerMessage('Downloading project...'));
    try {
      const fileResult = await designService.projects.exportProject(projectGuid, layoutOnly);
      fileResult.download();
    } catch (error) {
      dispatch(enqueueError('Error downloading project.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const uploadProject =
  (file: ReadFile, layoutOnly: boolean): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;
    if (openContext?.type !== 'project') {
      dispatch(enqueueError('Cannot upload layout data outside of a project context.'));
      return;
    }
    const { projectGuid } = openContext;

    const spinnerId = dispatch(addSpinnerMessage('Uploading project...'));
    try {
      await designService.projects.importPpf(projectGuid, file, layoutOnly);
      window.location.reload();
    } catch (error) {
      dispatch(enqueueError('Error uploading project.'));
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const saveAllProjectChangesWithSpinnerMessage =
  (action = 'saving project'): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (getState().application.openContext?.type !== 'project') {
      return;
    }

    const spinnerId = dispatch(addSpinnerMessage(`${capitalize(action)}...`));

    try {
      await dispatch(saveAllProjectChanges());
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Error ${action}.  Please contact Paragon Customer Support.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const receiveActiveProjectSynchronizationPayload =
  (payload: ProjectSynchronizationPayload): ThunkActionVoid =>
  (dispatch, getState) => {
    const context = getState().application.openContext;

    if (context?.type !== 'project') {
      throw new Error('Received project synchronization payload without an open project context.');
    }

    dispatch(
      projectSynchronized({
        [context.projectGuid]: produce(payload, (draft) => {
          draft.incomingModels = draft.incomingModels.map(migrateIncomingLayoutModel);
        }),
      }),
    );
  };

export const updateComponentDesignQuantity =
  (componentDesignGuid: string, newQuantity: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const existingInsertions = getInsertionsWithPendingChangesGroupedByObjectGuid(state)[componentDesignGuid];
    if (!existingInsertions) {
      throw new Error(`No insertions were found for component design ${componentDesignGuid}.`);
    }

    // layout insertions can't be edited numerically
    if (existingInsertions.some((insertion) => insertion.type === 'LayoutComponent')) {
      throw new Error('Attempted to change quantity of a layout component. The UI should prevent this.');
    }

    const parentGuids = uniq(existingInsertions.map((insertion) => insertion.parentGuid));
    if (parentGuids.length !== 1) {
      throw new Error('Attempted to change quantity of a component with multiple parents. The UI should prevent this.');
    }

    const insertion: Insertion = {
      type: 'NormalComponent',
      parentGuid: parentGuids[0],
      objectGuid: componentDesignGuid,
    };

    dispatch(changeInsertionQuantity(insertion, newQuantity - existingInsertions.length));
    await dispatch(saveAllProjectChanges());
  };
